<template>

    <Panel>

        <PanelHeader heading="Sale Dates and Times" subheading="When the sale is taking place" />

        <PanelBody>
            
            <Fieldset>

                <Message :active="state.sale_warning.active" type="error" :heading="state.sale_warning.message" class="mb-8"/>

                <div class="min-w-0 flex-1 px-0 md:grid md:grid-cols-2 md:gap-6 mb-6">
                    <div class="col-span-1">
                        <FormField :field="state.sale_form.fields.starts_at_date" namespace="sale_" :name="'starts_at_date'" class="mb-4" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="state.sale_form.fields.starts_at_time" namespace="sale_" :name="'starts_at_time'" class="mb-4" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="state.sale_form.fields.ends_at_date" namespace="sale_" :name="'ends_at_date'" class="mb-4" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="state.sale_form.fields.ends_at_time" namespace="sale_" :name="'ends_at_time'" class="mb-4" />
                    </div>
                </div>

                <div class="min-w-0 flex-1 px-0 md:grid md:grid-cols-2 md:gap-6">
                    <div class="col-span-1">
                        <FormField :field="state.sale_form.fields.first_lots_end_date" namespace="sale_" :name="'first_lots_end_date'" class="mb-4" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="state.sale_form.fields.first_lots_end_time" namespace="sale_" :name="'first_lots_end_time'" class="mb-4" />
                    </div>
                </div>

                <FormField :field="state.sale_form.fields.recurring_sale" namespace="sale_" :name="'recurring_sale'" class="mb-4" />

                <div v-show="state.sale_form.fields.repeat_frequency.active || state.sale_form.fields.repeat_up_to.active" class="min-w-0 flex-1 px-0 md:grid md:grid-cols-2 md:gap-6 mt-8">
                    <div class="col-span-1">
                        <FormField :field="state.sale_form.fields.repeat_frequency" namespace="sale_" :name="'repeat_frequency'" class="mb-4" />
                    </div>
                    <div class="col-span-1">
                        <FormField :field="state.sale_form.fields.repeat_up_to" namespace="sale_" :name="'repeat_up_to'" class="mb-4" />
                    </div>
                </div>
                
            </Fieldset>

        </PanelBody>

        <PanelFooter>
            <Button type="button" width="w-auto" class="ml-2" :onClick="() => next_step()" :disabled="!next_button_active">Next</Button>
        </PanelFooter>

    </Panel>

</template>

<script>
import { useSaleStore } from '@sto/sale.store'
import { useSystemStore } from '@sto/system.store'
import { useAuthStore } from '@sto/auth.store'
import { computed, onMounted, onBeforeMount, reactive, watch } from 'vue'
import { isValid, getPayload, getUserOrganisationType } from '@lib/helpers'
import { useRoute, useRouter } from 'vue-router'
import config from '@/config'
import LoadingIcon from '@ico/LoadingIcon.vue'
import moment from 'moment'
export default {
    components: {
        LoadingIcon
    },
    setup() {
        const ROUTE = useRoute()
        const ROUTER = useRouter()
        const SALE_STORE = useSaleStore()
        const SYSTEM_STORE = useSystemStore()
        const AUTH_STORE = useAuthStore()

        const sale = computed(() => SALE_STORE.sale)

        const duplicate_sale_template = computed(() => SALE_STORE.duplicate_sale_template)

        const sale_format = computed(() => {
            return sale.value?.format || null
        })

        const state = reactive({
            sale_form: {
                show_fields: false,
                valid: false,
                has_changes: computed(() => {
                    let has_changes = false
                    for (let f in state.sale_form.fields) {
                        if (state.sale_form.fields[f].value != state.sale_form.fields[f].stored_value) has_changes = true
                    }
                    return has_changes
                }),
                loading: false,
                fields: {
                    starts_at_date: {
                        active: true,
                        label: 'Sale start date',
                        placeholder: 'Select a Date',
                        type: 'datepicker',
                        value: sale?.value?.starts_at_date || null,
                        stored_value: '',
                        time_enabled: false,
                        min_date: new Date(),
                        date_format: 'dd-MM-yyyy',
                        date_preview_format: 'dd-MM-yyyy',
                        date_clearable: true,
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                    },
                    starts_at_time: {
                        active: true,
                        label: 'Sale start time',
                        type: 'text',
                        value: sale.value?.starts_at_time || (sale.value?.sale_type?.default_start_time || null),
                        stored_value: sale.value?.starts_at_time || null,
                        placeholder: 'e.g. 14:00',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    ends_at_date: {
                        active: true,
                        label: 'Sale end date',
                        placeholder: 'Select a Date',
                        type: 'datepicker',
                        value: sale.value?.ends_at_date || null,
                        stored_value: sale.value?.ends_at_date || null,
                        time_enabled: false,
                        min_date: new Date(),
                        date_format: 'dd-MM-yyyy',
                        date_preview_format: 'dd-MM-yyyy',
                        date_clearable: true,
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                    },
                    ends_at_time: {
                        active: true,
                        label: 'Sale end time',
                        type: 'text',
                        value: sale.value?.ends_at_time || (sale.value?.sale_type?.default_end_time || null),
                        stored_value: sale.value?.ends_at_time || null,
                        placeholder: 'e.g. 16:00',
                        required: false,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    first_lots_end_date: {
                        active: true,
                        label: 'First lots end date',
                        placeholder: 'Select a Date',
                        type: 'datepicker',
                        value: sale.value?.first_lots_end_date || null,
                        stored_value: sale.value?.first_lots_end_date || null,
                        time_enabled: false,
                        min_date: new Date(),
                        date_format: 'dd-MM-yyyy',
                        date_preview_format: 'dd-MM-yyyy',
                        date_clearable: true,
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                    },
                    first_lots_end_time: {
                        active: true,
                        label: 'First lots end time',
                        type: 'text',
                        value: sale.value?.first_lots_end_time || null,
                        stored_value: sale.value?.first_lots_end_time || null,
                        placeholder: 'e.g. 14:00',
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },

                    recurring_sale: {
                        active: true,
                        label: 'Recurring sale',
                        type: 'toggle',
                        value: sale.value?.recurring_sale == 1,
                        stored_value: null,
                        required: false,
                        pristine: true,
                        valid: true,
                        hide_hint: true,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: true,
                        disabled: false
                    },
                    repeat_frequency: {
                        active: sale.value?.recurring_sale == 1,
                        label: 'Repeat a sale like this',
                        type: 'select',
                        value: 'weekly',
                        stored_value: null,
                        options: [
                            { value: 'weekly', label: 'Same day, every week' },
                        ],
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                        editable: false,
                        disabled: false
                    },
                    repeat_up_to: {
                        active: sale.value?.recurring_sale == 1,
                        label: 'Up to and including',
                        placeholder: 'Select a Date',
                        type: 'datepicker',
                        value: sale.value?.repeat_up_to,
                        stored_value: null,
                        time_enabled: false,
                        min_date: new Date(),
                        date_format: 'dd-MM-yyyy',
                        date_preview_format: 'ddd Do M',
                        date_clearable: true,
                        required: true,
                        pristine: true,
                        valid: false,
                        message: '',
                        validation: [],
                        notes: '',
                    },
                }
            },
            sale_warning: {
                active: false,
                message: ''
            }
        })

        /**
         * should the next button be available to the user?
         */
         const next_button_active = computed(() => {
            let active = true
            for (let f in state.sale_form.fields) {
                if (state.sale_form.fields[f].active && state.sale_form.fields[f].required && !state.sale_form.fields[f].value) active = false
            }
            return active
        })

        const saveSaleTimes = async () => {

            if (state.sale_form.loading) return

            state.sale_form.loading = true

            if (!isValid(state.sale_form.fields)) {
                return state.sale_form.loading = false
            }

            const payload = getPayload(state.sale_form.fields)

            try {
                const sale = await SALE_STORE.setDraftSaleTimes(ROUTE.params.sale_id, payload)
                SALE_STORE.activateNextStep()

            } catch (error) {
                console.error('SALE_STORE.setDraftSaleTimes error', error)
                SYSTEM_STORE.addError('Something went wrong', error?.message || 'There was a problem saving your sale times')
            } finally {
                state.sale_form.loading = false
            }
        }

        const next_step = async () => {
            await saveSaleTimes()
        }

        /**
         * validate the selected dates and times to ensure they'r
         */
        const checkSelectedDateTimes = () => {
            state.sale_warning.active = false
            state.sale_warning.message = ''

            // do we have a start and end date?
            if (state.sale_form.fields.starts_at_date.value && state.sale_form.fields.ends_at_date.value) {
                // start date is after the end date
                if (moment(state.sale_form.fields.starts_at_date.value).isAfter(state.sale_form.fields.ends_at_date.value)) {
                    state.sale_warning.active = true
                    state.sale_warning.message += (state.sale_warning.message.length ? ' ':'') + 'The end date must be after the start date'
                }

                // start and ends on same day
                if (moment(state.sale_form.fields.starts_at_date.value).isSame(state.sale_form.fields.ends_at_date.value, 'day') && state.sale_form.fields.starts_at_time.value && state.sale_form.fields.ends_at_time.value) {
                    // end time is before start time
                    // NOTE: we allow end time to be before start time if the sale ends on a different day
                    if (moment(state.sale_form.fields.starts_at_date.value+' '+state.sale_form.fields.starts_at_time.value).isAfter(state.sale_form.fields.ends_at_date.value+' '+state.sale_form.fields.ends_at_time.value)) {
                        state.sale_warning.active = true
                        state.sale_warning.message += (state.sale_warning.message.length ? ' ':'') + 'The end time must be after the start time'
                    }
                }
            }

            return
        }

        watch(
            () => state.sale_form.fields.starts_at_date.value,
            (new_value, old_value) => {
                state.sale_form.fields.ends_at_date.min_date = !new_value ? new Date() : new Date(new_value)
            }
        )

        watch(
            () => state.sale_form.fields.starts_at_date.value,
            (new_value, old_value) => {

                // if there is no currently set ends at date AND we have a reference sale
                if (!state.sale_form.fields.ends_at_date.value && duplicate_sale_template.value?.starts_at_date && duplicate_sale_template.value?.ends_at_date) {

                    let sale_start_to_sale_end_days_diff = moment(duplicate_sale_template.value.ends_at_date).diff(moment(duplicate_sale_template.value.starts_at_date), 'days')
                    let new_sale_end_date = moment(state.sale_form.fields.starts_at_date.value).add(sale_start_to_sale_end_days_diff, 'days').format('YYYY-MM-DD')
                    state.sale_form.fields.ends_at_date.value = new_sale_end_date
                }

                if (!state.sale_form.fields.first_lots_end_date.value && duplicate_sale_template.value?.starts_at_date && duplicate_sale_template.value?.first_lots_end_date) {

                    let sale_start_to_first_lots_end_days_diff = moment(duplicate_sale_template.value.first_lots_end_date).diff(moment(duplicate_sale_template.value.starts_at_date), 'days')
                    let new_first_lots_end_date = moment(state.sale_form.fields.starts_at_date.value).add(sale_start_to_first_lots_end_days_diff, 'days').format('YYYY-MM-DD')
                    state.sale_form.fields.first_lots_end_date.value = new_first_lots_end_date
                }

                state.sale_form.fields.starts_at_date.max_date = !new_value ? new Date() : new Date(new_value)
                
                const repeat_date = !new_value ? new Date() : new Date(new_value)

                switch(state.sale_form.fields.repeat_frequency.value) {
                    case 'weekly':
                        repeat_date.setDate(repeat_date.getDate()+7)
                        break
                }

                state.sale_form.fields.repeat_up_to.min_date = repeat_date
            }
        )

        watch(
            () => [
                state.sale_form.fields.starts_at_date.value,
                state.sale_form.fields.starts_at_time.value,
                state.sale_form.fields.ends_at_date.value,
                state.sale_form.fields.ends_at_time.value
            ],
            () => {
                checkSelectedDateTimes()
            }
        )

        watch(
            () => state.sale_form.fields.recurring_sale.value,
            (new_value, old_value) => {
                state.sale_form.fields.repeat_frequency.active = new_value
                state.sale_form.fields.repeat_frequency.required = new_value
                state.sale_form.fields.repeat_up_to.active = new_value
                state.sale_form.fields.repeat_up_to.required = new_value
            }
        )

        return {
            SALE_STORE,
            ROUTER,
            next_button_active,
            state,
            sale,
            sale_format,
            next_step
        }
    }
}

</script>

